import React, { useState, useCallback, useEffect } from 'react';
import Filter from '@/pages/intelligence/components/Filter';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useRadioOptionsTipsGetterOfRegionSelect from '@intelligence/hooks/useRadioOptionsTipsGetterOfRegionSelect';
import { toDateTypeLy } from '@intelligence/utils/common';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';

export default function OverviewFilter(props: any) {
  const { filterConfirm, onFilterInit, defaultDate, entityType, initialValue } = props;
  const isMobile = entityType === 'mobile';

  // console.log('OverviewFilter', props);

  const getRadioOptionsTips = useRadioOptionsTipsGetterOfRegionSelect(['pc', 'console'].includes(entityType));

  const defaultValue: any = {
    [filterKeyMap.REGION]: {
      regions: [],
      markets: ['global'],
      type: 'market',
    },
    [filterKeyMap.TIME_TYPE]: {
      value: defaultDate,
    },
  };
  if (isMobile) {
    defaultValue[filterKeyMap.REVENUE_SCOPE] = {
      value: REVENUE_SCOPE_VALUES.EXCLUDING,
    };
  }
  // 继承逻辑在这写
  // formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});

  let initialFormValue = defaultValue;
  if (isOperateDetail) {
    initialFormValue = {
      [filterKeyMap.REGION]: {
        regions:
          detailFilterExtend[filterKeyMap.REGION]?.filterRegions ??
          defaultValue[filterKeyMap.REGION]?.regions,
        markets:
          detailFilterExtend[filterKeyMap.REGION]?.filterMarkets ??
          defaultValue[filterKeyMap.REGION]?.markets,
        type:
          detailFilterExtend[filterKeyMap.REGION]?.filterType ??
          defaultValue[filterKeyMap.REGION]?.type,
      },
      [filterKeyMap.TIME_TYPE]: {
        value: detailFilterExtend[filterKeyMap.TIME_TYPE]?.value ?? defaultDate,
      },
    };
    if (isMobile) {
      initialFormValue[filterKeyMap.REVENUE_SCOPE] = {
        value:
          detailFilterExtend[filterKeyMap.REVENUE_SCOPE]?.value ??
          defaultValue[filterKeyMap.REVENUE_SCOPE]?.value,
      };
    }
  }
  const mergedInitialFormValue: any = {
    ...initialFormValue,
    ...initialValue,
    [filterKeyMap.REGION]: {
      ...initialFormValue?.[filterKeyMap.REGION],
      ...initialValue?.[filterKeyMap.REGION],
    },
    [filterKeyMap.TIME_TYPE]: {
      ...initialFormValue?.[filterKeyMap.TIME_TYPE],
      ...initialValue?.[filterKeyMap.TIME_TYPE],
    },
  };
  if (isMobile) {
    mergedInitialFormValue[filterKeyMap.REVENUE_SCOPE] = {
      ...initialFormValue?.[filterKeyMap.REVENUE_SCOPE],
      ...initialValue?.[filterKeyMap.REVENUE_SCOPE],
    };
  } else {
    delete mergedInitialFormValue[filterKeyMap.REVENUE_SCOPE];
  }

  const [formValue, setFormValue] = useState(mergedInitialFormValue);

  const updateInputList = (defaultDate: boolean) => {
    const nextInputList: any[] = [
      {
        key: filterKeyMap.TIME_TYPE,
        default: {
          value: defaultDate || 'day',
        },
      },
      {
        key: filterKeyMap.REGION,
        default: defaultValue[filterKeyMap.REGION],
        visible: 1,
        options: [],
        disableNoChina: entityType !== 'mobile', // mobile的不禁用，PC/Console要禁用
        entityType,
      },
    ];
    if (isMobile) {
      nextInputList.push({
        key: filterKeyMap.REVENUE_SCOPE,
        default: defaultValue[filterKeyMap.REVENUE_SCOPE],
        controlType: 'switch',
      });
    }
    return nextInputList;
  };
  const [inputList, setInputList] = useState(updateInputList(defaultDate));

  /** 优化TODO：这一整块及inputList里的国家参数都放到model里 */
  const { filterLoading, regionOptions } = useMarketFilterApi({});

  useEffect(() => {
    if (onFilterInit instanceof Function) {
      onFilterInit({
        loading: filterLoading,
        regionOptions,
      });
    }
  }, [filterLoading, regionOptions]);

  useEffect(() => {
    setFormValue(mergedInitialFormValue);
  }, [JSON.stringify(mergedInitialFormValue)]);

  useEffect(() => {
    /** 国家筛选器的列表值 */
    const inputListTmp = [...inputList];
    const regionIndex = inputList?.findIndex((item) => {
      return item?.key === filterKeyMap.REGION;
    });
    if (regionIndex !== -1) {
      if (regionOptions) {
        inputListTmp[regionIndex].options = regionOptions;
      }
      if (!!getRadioOptionsTips) {
        // @ts-ignore
        inputListTmp[regionIndex].getRadioOptionsTips = getRadioOptionsTips;
      }
      setInputList(inputListTmp);
    }
  }, [regionOptions, getRadioOptionsTips]);

  const onConfirm = useCallback(
    (value: any /** 筛选器数据 */, apiValue: any /** 筛选器转换后的数据 */) => {
      setFormValue(value);
      const granularity = toDateTypeLy[value?.[filterKeyMap.TIME_TYPE]?.value || ''];
      const markets = apiValue?.[filterKeyMap.REGION]?.markets || [];
      const filterForm: any = {
        region_type: apiValue[filterKeyMap.REGION]?.region_type,
        regions: apiValue[filterKeyMap.REGION]?.regions,
        date_type: apiValue[filterKeyMap.TIME_TYPE]?.value,
      };
      const urlParams: any = {
        ...(granularity ? { granularity } : {}),
        ...(markets.length > 0 ? { region: markets.join(',') } : {}),
      };
      const removeUrlParams = [
        ...(granularity ? [] : ['granularity']),
        ...(markets.length > 0 ? [] : ['region']),
      ];
      if (isMobile) {
        const revenueScope = apiValue?.[filterKeyMap.REVENUE_SCOPE]?.value || REVENUE_SCOPE_VALUES.EXCLUDING;
        filterForm.revenue_scope = revenueScope;
        urlParams.revenueScope = revenueScope;
      } else {
        removeUrlParams.push('revenueScope');
      }
      updateURLSearchParams(urlParams, removeUrlParams);
      filterConfirm(filterForm, value);
      /** TODO 时间筛选器类型继承 */
      setDetailFilterExtend(apiValue);
    },
    [filterConfirm],
  );

  return (
    <Filter
      value={formValue}
      inputList={inputList}
      // onInit={onInit}
      onConfirm={onConfirm}
      showButton={false}
    />
  );
}
